import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import qs from 'qs';

type QueryParams = Record<string, any>;

const updateParams = (
  params: QueryParams,
  key: string,
  value: any
): QueryParams => {
  if (value === null) {
    const { [key]: omit, ...rest } = params;
    return rest;
  }
  return { ...params, [key]: value };
};

const getParams = (str: string): QueryParams =>
  qs.parse(str, {
    ignoreQueryPrefix: true,
  });

const setParams = (obj: QueryParams): string =>
  qs.stringify(obj, {
    arrayFormat: 'indices',
    encodeValuesOnly: true,
  });

interface UseQueryStringOptions<T> {
  validate?: (value: T) => boolean;
  serialize?: (value: T) => any;
  parse?: (value: any) => T;
}

/**
 * Updates query string for specific key only
 *
 * @param key - specific key of param we want to update
 * @param defaultValue - value to use in STATE when value from URL is empty or invalid
 * @param opts - options object containing validate, serialize, and parse functions
 * @returns tuple of [value, setValue] where value is the current value and setValue is a function to update it
 *
 * The state returned is now of type T | null so that calling setValue(null) is allowed.
 */
export default function useQueryString<T>(
  key: string,
  defaultValue: T,
  opts: UseQueryStringOptions<T> = {}
): [T | null, Dispatch<SetStateAction<T | null>>] {
  const { validate = () => true, parse, serialize } = opts;
  const navigate = useNavigate();
  const location = useLocation();

  const { [key]: rawValue } = getParams(location.search);

  const initialValue =
    typeof parse === 'function'
      ? parse(rawValue || defaultValue)
      : (rawValue as T | undefined) ?? defaultValue;
  const isValid = initialValue !== undefined && validate(initialValue);

  const [value, setValue] = useState<T | null>(
    isValid ? initialValue : defaultValue
  );

  useEffect(() => {
    const params = getParams(location.search);
    const current = params[key] ?? null;
    const next =
      typeof serialize === 'function' ? serialize(value as T) : value;

    if (current !== next) {
      const result = updateParams(params, key, next);
      navigate(`?${setParams(result)}`, { replace: true });
    }
  }, [location.search, key, serialize, value, navigate]);

  return [value, setValue];
}
